import { useState, useRef, useCallback } from "react";

/**
 * useTicketForm - A specialized form hook for ticket-based modules
 * 
 * Extends the hybrid state/refs pattern with ticket-specific conveniences:
 * - Automatic loading of existing ticket data
 * - Locked/read-only state management
 * - Edited tracking for unsaved changes detection
 * - Data loaded tracking for conditional rendering
 * - Integration with ticket context for refresh after save
 * 
 * @example
 * const {
 *   formData,
 *   setFormData,
 *   refs,
 *   isLocked,
 *   isEdited,
 *   isLoading,
 *   dataLoaded,
 *   handleSubmit,
 *   isSubmitting,
 *   loadTicket,
 *   markEdited,
 * } = useTicketForm({
 *   initialState: { description: "", comments: "", line_items: [] },
 *   initialRefs: { report_type: "default", generate_pdf: false },
 *   onSubmit: async (payload) => {
 *     await saveTicket(payload);
 *   },
 *   onAfterSubmit: async () => {
 *     await refreshItems();
 *     navigate("/tickets");
 *   },
 * });
 * 
 * @param {Object} config
 * @param {Object} config.initialState - Reactive state values (affect UI re-renders)
 * @param {Object} config.initialRefs - Non-reactive values (won't trigger re-renders)
 * @param {Function} config.onSubmit - Async function called with combined payload on submit
 * @param {Function} [config.onAfterSubmit] - Optional callback after successful submit (e.g., refresh, navigate)
 * @param {Function} [config.transformLoadedData] - Optional function to transform loaded data before setting state
 * 
 * @returns {Object} Form state and handlers
 */
export function useTicketForm({
  initialState = {},
  initialRefs = {},
  onSubmit,
  onAfterSubmit,
  transformLoadedData,
}) {
  // Form data state (reactive - affects UI)
  const [formData, setFormData] = useState(initialState);
  
  // Ref-based data (non-reactive - for API submission)
  const refs = useRef({ ...initialRefs });
  
  // Form status states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  
  // Store initial loaded data for comparison/revert
  const initialLoadedData = useRef(null);

  /**
   * Load existing ticket data
   * Splits data into state (UI-affecting) and refs (submission-only) based on initialState keys
   * 
   * @param {Object} data - Ticket data from API
   * @param {Object} [options] - Options for loading
   * @param {boolean} [options.locked] - Whether the ticket is locked
   * @param {Object} [options.refOverrides] - Additional ref values to set
   */
  const loadTicket = useCallback((data, options = {}) => {
    if (!data) return;
    
    setIsLoading(true);
    
    try {
      // Store original data for comparison
      initialLoadedData.current = data;
      
      // Transform data if transformer provided
      const transformedData = transformLoadedData 
        ? transformLoadedData(data) 
        : data;
      
      // Split data: keys in initialState go to state, rest go to refs
      const stateKeys = Object.keys(initialState);
      const newState = {};
      const newRefs = { ...initialRefs };
      
      Object.entries(transformedData).forEach(([key, value]) => {
        if (stateKeys.includes(key)) {
          newState[key] = value;
        } else {
          newRefs[key] = value;
        }
      });
      
      // Apply ref overrides
      if (options.refOverrides) {
        Object.assign(newRefs, options.refOverrides);
      }
      
      // Update state and refs
      setFormData(prev => ({ ...prev, ...newState }));
      Object.assign(refs.current, newRefs);
      
      // Set locked status
      setIsLocked(options.locked ?? data.locked ?? false);
      
      // Mark data as loaded
      setDataLoaded(true);
      
      // If there's existing data (like line_items), mark as edited
      if (data.line_items?.length > 0) {
        setIsEdited(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [initialState, initialRefs, transformLoadedData]);

  /**
   * Mark form as edited (call when user makes changes)
   */
  const markEdited = useCallback(() => {
    setIsEdited(true);
  }, []);

  /**
   * Reset edited state (e.g., after reload from external source)
   */
  const clearEdited = useCallback(() => {
    setIsEdited(false);
  }, []);

  /**
   * Update locked status
   */
  const setLocked = useCallback((locked) => {
    setIsLocked(locked);
  }, []);

  /**
   * Handle form field changes (for controlled inputs with data-ref attribute)
   */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked, dataset } = e.target;
    const isRef = dataset?.ref === "true";

    if (isRef) {
      refs.current[name] = type === "checkbox" ? checked : value;
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
      setIsEdited(true);
    }
  }, []);

  /**
   * Handle form submission
   * Combines state and refs, calls onSubmit, then onAfterSubmit
   */
  const handleSubmit = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    
    const payload = { ...formData, ...refs.current };

    try {
      setIsSubmitting(true);
      await onSubmit(payload);
      
      if (onAfterSubmit) {
        await onAfterSubmit(payload);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onSubmit, onAfterSubmit]);

  /**
   * Get original loaded data for comparison
   */
  const getInitialData = useCallback(() => {
    return initialLoadedData.current;
  }, []);

  /**
   * Check if form has unsaved changes by comparing to initial loaded data
   */
  const hasUnsavedChanges = useCallback(() => {
    if (!initialLoadedData.current) return isEdited;
    return JSON.stringify(formData) !== JSON.stringify(
      Object.keys(formData).reduce((acc, key) => {
        acc[key] = initialLoadedData.current[key];
        return acc;
      }, {})
    );
  }, [formData, isEdited]);

  /**
   * Reset form to initial loaded data
   */
  const resetToInitial = useCallback(() => {
    if (initialLoadedData.current) {
      loadTicket(initialLoadedData.current);
      setIsEdited(false);
    }
  }, [loadTicket]);

  /**
   * Set a specific ref value
   */
  const setRef = useCallback((key, value) => {
    refs.current[key] = value;
  }, []);

  /**
   * Get a specific ref value
   */
  const getRef = useCallback((key) => {
    return refs.current[key];
  }, []);

  return {
    // State
    formData,
    setFormData,
    refs,
    
    // Status
    isSubmitting,
    isLoading,
    dataLoaded,
    isEdited,
    isLocked,
    
    // Handlers
    handleChange,
    handleSubmit,
    
    // Ticket-specific utilities
    loadTicket,
    markEdited,
    clearEdited,
    setLocked,
    getInitialData,
    hasUnsavedChanges,
    resetToInitial,
    setRef,
    getRef,
  };
}

export default useTicketForm;
